import { useDispatch, useSelector } from "react-redux";

import { DataScreen, ScreenContent } from "components/base/DataScreenBase";
import { shopkeeperAnimation } from "components/styles/animations";
import { media } from "components/styles/breakpoints";
import { getBuildingData } from "libs/stats";
import { exitBuilding, switchBuildingScreen } from "redux/actions";
import { getBuildingInfo } from "redux/selectors/characterSelectors";
import styled, { css } from "styled-components";
import { BuildingScreen } from "types";
import Tooltip, { TooltipDialog, TooltipName } from "./Tooltip";

interface BuildingInteriorDisplayProps {
  building: string;
  screens: BuildingScreen[];
}

interface MenuItemProps {
  isActive: boolean;
}

export const InteriorWrapper = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
`;

export const MenuWrapper = styled.div`
  background-color: #0e4042;
  color: white;
  z-index: 1;
  display: flex;
  opacity: 0.95;
  border-bottom: 6px #43fffa solid;
  min-height: 60px;
`;

export const BuildingName = styled.div`
  font-size: 20px;
  color: #43fffa;
  text-transform: uppercase;
  font-weight: 600;
  background: #000000;
  white-space: nowrap;
  display: flex;
  align-items: center;
  padding-left: 15px;

  ${media.desktop`
    font-size: 28px;
    padding-left: 30px;
  `}
`;

export const activeMixin = css`
  background-color: #00000020;
  color: #43fffa;
`;

export const MenuItem = styled.div<MenuItemProps>`
  font-size: 14px;
  text-transform: uppercase;
  font-weight: 600;
  cursor: pointer;
  box-sizing: border-box;
  padding: 0 15px;
  display: flex;
  align-items: center;
  ${(props) => props.isActive && activeMixin};

  ${media.desktop`
    font-size: 20px;
    padding: 0 30px;
  `}

  &:hover {
    background-color: #00000020;
  }
`;

export const ExitButton = styled.div`
  margin-left: auto;
  font-size: 14px;
  text-transform: uppercase;
  font-weight: 600;
  cursor: pointer;
  box-sizing: border-box;
  color: #ffffff;
  padding: 0 15px;
  background-color: #ffffff20;
  display: flex;
  align-items: center;

  ${media.desktop`
    font-size: 20px;
    padding: 0 30px;
  `}

  &:hover {
    background-color: #00000020;
  }
`;

export const ContentWrapper = styled.div`
  display: flex;
  align-items: stretch;
  flex-grow: 1;
  overflow-y: scroll;
`;

export const DataScreenWrapper = styled.div`
  flex-basis: 100%;
`;

export const StaffWrapper = styled.div`
  height: 100%;
  padding-left: 10px;
  padding-right: 20px;
  display: flex;
  align-items: flex-end;
`;

export const StaffImageWrapper = styled.div`
  height: 90%;
  overflow: hidden;
`;

export const InteriorStaff = styled.img`
  display: block;
  height: 100%;

  animation: ${shopkeeperAnimation} 3s ease-in-out infinite;
  transform-origin: bottom;
`;

export default function BuildingInteriorDisplay({
  building,
  screens,
}: BuildingInteriorDisplayProps) {
  const dispatch = useDispatch();

  const { buildingScreen, buildingDialog } = useSelector(getBuildingInfo);

  const { name, staffImage, staffName } = getBuildingData(building);

  const availableScreens = screens.filter(
    (screen) => !(screen.shouldHide && screen.shouldHide())
  );

  const setScreen = availableScreens.find(
    (screen) => screen.slug === buildingScreen
  );
  const currentScreen = setScreen ? setScreen : availableScreens[0];

  return (
    <InteriorWrapper>
      <MenuWrapper>
        <BuildingName>
          {name}
          <StaffWrapper>
            <StaffImageWrapper>
              <Tooltip
                content={
                  <>
                    <TooltipName>{staffName}</TooltipName>
                    <TooltipDialog>
                      {!!buildingDialog ? buildingDialog : currentScreen.dialog}
                    </TooltipDialog>
                  </>
                }
              >
                <InteriorStaff src={staffImage} />
              </Tooltip>
            </StaffImageWrapper>
          </StaffWrapper>
        </BuildingName>

        {availableScreens.map((screen) => (
          <MenuItem
            key={screen.slug}
            isActive={currentScreen.slug === screen.slug}
            onClick={() => dispatch(switchBuildingScreen(screen.slug))}
          >
            {screen.name}
          </MenuItem>
        ))}

        <ExitButton onClick={() => dispatch(exitBuilding())}>Exit</ExitButton>
      </MenuWrapper>
      <ContentWrapper>
        <DataScreenWrapper>
          <DataScreen>
            {availableScreens.map((screen) => (
              <ScreenContent
                key={screen.slug}
                isShowing={currentScreen.slug === screen.slug}
              >
                {screen.component}
              </ScreenContent>
            ))}
          </DataScreen>
        </DataScreenWrapper>
      </ContentWrapper>
    </InteriorWrapper>
  );
}
